import {
  AssociatedFile,
  EmailTemplate,
  EmailTemplateStatus,
  CustomValues,
  EmailTemplateType,
} from '@alucio/aws-beacon-amplify/src/models';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { commonReducers, initialState, SliceState } from './common';
import ActiveUser from '../../global/ActiveUser';
import { DataStore } from 'aws-amplify/datastore';

const sliceName = 'emailTemplate';
const { reducers, extraReducers } = commonReducers<EmailTemplate>(sliceName);

interface CreateEmailTemplatePayload {
  title: string;
  subject: string;
  cc: string[];
  bcc: string[];
  body: string;
  customFilterValues: CustomValues[];
  associatedFiles: AssociatedFile[];
  status: EmailTemplateStatus;
  type: EmailTemplateType;
  callback: (emailTemplate: EmailTemplate) => void
}

const emailTemplateSlice = createSlice({
  name: sliceName,
  initialState: initialState<EmailTemplate>(),
  reducers: {
    ...reducers,
    create: (
      _state: SliceState<EmailTemplate>,
      action: PayloadAction<CreateEmailTemplatePayload>,
    ): void => {
      const { payload } = action;
      const user = ActiveUser.user;

      if (!user) {
        throw new Error('Could not get current user');
      }
      const now = new Date().toISOString();
      const { callback, ...emailDataFromPayload } = payload;

      DataStore.save(
        new EmailTemplate({
          ...emailDataFromPayload,
          tenantId: user.tenantId,
          createdAt: now,
          createdBy: user.id,
          updatedAt: now,
          updatedBy: user.id,
          editPermissions: [`TenantPublisher-${user.tenantId}`],
        }),
      ).then(callback);
    },
    deactivate: {
      prepare: (emailTemplate: EmailTemplate) => {
        analytics?.track('EMAIL_DEACTIVATE', {
          action: 'EMAIL_DEACTIVATE',
          category: 'EMAIL',
          emailTemplateId: emailTemplate?.id,
        });
        return {
          payload: {
            model: EmailTemplate,
            entity: emailTemplate,
            updates: { status: EmailTemplateStatus.DEACTIVATED },
          },
        }
      },
      reducer: reducers.save,
    },
    delete: {
      prepare: (emailTemplate: EmailTemplate) => {
        analytics?.track('EMAIL_DELETE', {
          action: 'EMAIL_DELETE',
          category: 'EMAIL',
          emailTemplateId: emailTemplate?.id,
        });
        return {
          payload: {
            model: EmailTemplate,
            entity: emailTemplate,
            updates: { status: EmailTemplateStatus.REMOVED },
          },
        }
      },
      reducer: reducers.save,
    },
  },
  extraReducers,
});

export default emailTemplateSlice;
export const emailTemplateActions = {
  ...emailTemplateSlice.actions,
};
